import { AgentRun } from '@lucidtech/las-sdk-browser';
import { useCallback, useMemo } from 'react';

import { CREATE_PREDICTION_FUNCTION_ID } from '@/constants';
import { useRerunAgentRun } from '@/hooks';
import { toActionId, toAgentId, useBatchGetActions, useGetAgent } from '@/hooks/api';
import { isActionId, isActionRunId } from '@/utils';

type UseRerunCallbacksProps = {
  agentId?: string | null;
  selectedRunIds?: Set<string> | null;
  onComplete?: () => void;
};

export const useRerunCallbacks = ({ agentId, selectedRunIds, onComplete }: UseRerunCallbacksProps) => {
  const { rerun, ...status } = useRerunAgentRun({ agentId });

  const { data: agent } = useGetAgent(toAgentId(agentId));
  const { data: actions } = useBatchGetActions(agent?.resourceIds.filter(isActionId).map(toActionId));

  const createPredictionAction = useMemo(
    () => actions?.find((action) => action.functionId === CREATE_PREDICTION_FUNCTION_ID),
    [actions]
  );

  const onRerun = useCallback(
    async (selectActionFn: (agentRun: AgentRun) => string | undefined) => {
      if (selectedRunIds == null) {
        onComplete?.();
        return;
      }

      try {
        await rerun([...selectedRunIds], selectActionFn);
        onComplete?.();
      } catch (error) {
        console.error(error);
      }
    },
    [onComplete, rerun, selectedRunIds]
  );

  const onRerunFromStart = useCallback(async () => {
    onRerun((agentRun: AgentRun) => {
      const lastRun = agentRun.events
        .filter((event) => isActionRunId(event.resourceId))
        .findLast((event) => event.actionId === createPredictionAction?.actionId);

      return lastRun?.resourceId;
    });
  }, [createPredictionAction?.actionId, onRerun]);

  const onRerunFromExport = useCallback(async () => {
    const exportActions = actions?.filter((action) => action.functionId?.includes('export'));

    for (const exportAction of exportActions || []) {
      await onRerun((agentRun: AgentRun) => {
        const startIndex = agentRun.events.findLastIndex(
          (event) => event.actionId === createPredictionAction?.actionId
        );

        const lastRun = agentRun.events
          .filter((event) => isActionRunId(event.resourceId))
          .find((event) => exportAction.actionId === event.actionId, startIndex);

        return lastRun?.resourceId;
      });
    }
  }, [actions, createPredictionAction?.actionId, onRerun]);

  return { onRerunFromStart, onRerunFromExport, ...status };
};
